// B5 (Launch-Batch-2, 2026-06-10) — SSO-Hint-Protokoll + Entscheidungslogik.
//
// Geschichte: v1 des Handoffs (5c28e56) kopierte den oidc-userString des
// PORTAL-Clients (scrolly-admin) per postMessage in den Viewer-sessionStorage
// und reloadete. Zwei strukturelle Probleme:
//   1. Der iframe-Pfad loopte (onLoad → send → storage-write + reload →
//      onLoad → …, Revert 4ff204d „Zucken").
//   2. Das kopierte Token gehörte zum falschen Client: accessTokenLifespan
//      ist 300s, und der Renew mit client_id=scrolly-public gegen ein
//      scrolly-admin-Refresh-Token wäre an Keycloak gescheitert →
//      nach ≤5 Minuten wieder anonym (B6-Symptomatik zurück).
//
// v2 (dieses Modul): Das Portal sendet nur noch einen HINT („es existiert
// eine Keycloak-SSO-Session"). Der Viewer macht daraufhin EINMAL einen
// top-level signinRedirect (prompt=none) mit seinem EIGENEN Client
// scrolly-public → echtes Token inkl. Refresh, automaticSilentRenew läuft
// über den Token-Endpoint (kein iframe → kein Safari-ITP-Problem).
// Kein storage-write fremder Tokens, kein reload-on-message — der
// 29.05.-Loop-Motor existiert in v2 nicht mehr.
//
// Sync-Hinweis: Message-Typen sind dupliziert in
// portal/src/lib/sso-handoff.ts (eigene Workspaces, kein Shared-Package).
// Drift-Lock: sso-hint.test.ts pinnt die Literale.

/** Message-Typ Portal → Viewer. */
export const SSO_HANDOFF_MESSAGE_TYPE = "scrolly-sso-handoff";
/** Ack Viewer → Portal (stoppt das Sender-Polling). */
export const SSO_ACK_MESSAGE_TYPE = "scrolly-sso-ack";

/**
 * sessionStorage-Flag: in diesem Tab wurde der Hint-Redirect bereits
 * versucht. sessionStorage ist per-Tab und überlebt den Redirect-
 * Roundtrip (view → auth.scrolly.com → view) — perfekte
 * once-Semantik gegen jede Wiederholungs-Schleife.
 */
export const SSO_ATTEMPTED_KEY = "scrolly:sso-hint-attempted";

/**
 * sessionStorage: return_to für den Silent-Fail-Pfad. Primär reist
 * return_to im OIDC url_state; bei prompt=none-Fehlern (login_required)
 * ist der Zugriff auf url_state lib-abhängig — dieser Key ist der
 * robuste Fallback für oidc-callback/+Page.tsx.
 */
export const SSO_RETURN_TO_KEY = "scrolly:sso-hint-return-to";

export type SsoHintDecision = "ignore" | "ack-only" | "redirect";

/**
 * Pure Entscheidungslogik des Receivers — testbar ohne React/DOM.
 *
 * - "ignore":   Origin nicht in der Allowlist → gar nicht reagieren
 *               (auch kein Ack an fremde Origins).
 * - "ack-only": legitimer Sender, aber kein Redirect — Polling stoppen.
 * - "redirect": einmaliger top-level signinRedirect (prompt=none).
 */
export function decideSsoHint(input: {
  originAllowed: boolean;
  /** window.self === window.top — im iframe wird NIE redirected (Loop-Klasse vom 29.05.). */
  isTopWindow: boolean;
  /** SSO_ATTEMPTED_KEY bereits gesetzt (once pro Tab). */
  attempted: boolean;
  isAuthenticated: boolean;
}): SsoHintDecision {
  if (!input.originAllowed) return "ignore";
  if (!input.isTopWindow) return "ack-only";
  if (input.attempted) return "ack-only";
  if (input.isAuthenticated) return "ack-only";
  return "redirect";
}

/** OAuth-Fehlercodes, die bei prompt=none ein erwarteter Silent-Fail sind. */
export const SILENT_SSO_FAIL_ERRORS = new Set([
  "login_required",
  "interaction_required",
  "consent_required",
  "account_selection_required",
]);
